// Record-specific subfolders (User Story 4). A subfolder is a free-text,
// one-level name stored on a file. It belongs to one record and one file type,
// and exists only while at least one non-deleted file is in it.
export const SUBFOLDER_NAME_MAX_LENGTH = 100

// PATCH file-attachment/subfolder accepts at most this many file ids per request.
export const MOVE_FILES_MAX_COUNT = 100

// Shown before a subfolder in every path: "Addenda › Addendum 3" (D16).
export const SUBFOLDER_SEPARATOR = '›'

// Splits a file type name into parent and file type: "Sales>Quotes". It is part
// of the stored name; the backend gives it no meaning.
export const FILE_TYPE_PARENT_SEPARATOR = '>'

export const SUBFOLDER_FORBIDDEN_CHARACTERS = ['>', '›', '/']

// Visual treatment of FileTypeChip: neutral keeps the whole path in the chip's
// own colours (sidebar); accent renders the subfolder as a lilac badge.
export const FILE_TYPE_CHIP_VARIANTS = { neutral: 'neutral', accent: 'accent' } as const
export type FileTypeChipVariant = typeof FILE_TYPE_CHIP_VARIANTS[keyof typeof FILE_TYPE_CHIP_VARIANTS]

// Search box placeholders. The sidebar always shows the short one; the Files
// tab shows the long one when subfolders are enabled.
export const DOCS_SEARCH_PLACEHOLDER = 'Search File Names'
export const DOCS_SEARCH_WITH_SUBFOLDERS_PLACEHOLDER = 'Search File Names and Subfolders'

// VActions option value of the "Move to subfolder" file action.
export const FILE_ACTION_MOVE_TO_SUBFOLDER = 'moveToSubfolder'

// The picker needs room for its search box and buttons; the plain menu is narrower.
export const SUBFOLDER_PICKER_MIN_WIDTH = '300px'

// VSelect cannot tell "chose No subfolder" from "chose nothing" when both are
// null, so the "No subfolder" option carries this sentinel instead.
export const SUBFOLDER_NONE_OPTION_VALUE = '__no_subfolder__'

// Fallback for "this {entity} only" when the record entity's title (e.g. "Project") is unknown.
export const SUBFOLDER_DEFAULT_RECORD_NAME = 'record'

// Non-selectable rows of the upload dropdown: the section header and the
// "None yet" line. Their values are never emitted.
export const SUBFOLDER_HEADER_OPTION_VALUE = '__header__'
export const SUBFOLDER_EMPTY_OPTION_VALUE = '__empty__'

const pluralizeFiles = (count: number): string => `${count} ${count === 1 ? 'file' : 'files'}`

export const SUBFOLDER_MESSAGES = {
  fieldLabel: 'Subfolder',
  fieldPlaceholder: 'Type to find or create a subfolder',
  fieldPlaceholderDisabled: 'Choose a file type first',
  sectionHeader: (fileTypeName: string, entityTitle: string): string => `In ${fileTypeName} on this ${entityTitle.toLowerCase()}`,
  noSubfoldersYet: 'None yet. Type a name to create one.',
  fieldHint: (recordName: string): string => `Optional · only on ${recordName}`,
  fieldHintWithoutRecord: 'Optional',
  createOption: (name: string): string => `Create “${name}”`,
  noSubfolderOption: (fileTypeName: string): string => `No subfolder (file directly in ${fileTypeName})`,
  noSubfolderShort: 'No subfolder',
  savesAs: 'Saves as',
  fileCount: pluralizeFiles,
  tooLong: `Use ${SUBFOLDER_NAME_MAX_LENGTH} characters or fewer.`,
  forbiddenCharacters: `Subfolder names can’t contain ${SUBFOLDER_FORBIDDEN_CHARACTERS.join(' ')}.`,
  moveOption: 'Move to subfolder',
  moveGroupOption: 'Move files to subfolder',
  mixedFileTypes: 'Select files of one file type.',
  tooManyFiles: `Select ${MOVE_FILES_MAX_COUNT} files or fewer.`,
  pickerTitleSingle: 'Move to subfolder',
  pickerTitleMany: (count: number): string => `Move ${count} files to a subfolder`,
  pickerSubtitle: (fileTypeName: string, recordName: string): string => `Under ${fileTypeName} · this ${recordName} only`,
  pickerSearchPlaceholder: 'Find or create a subfolder',
  backToActions: 'Back to actions',
  cancel: 'Cancel',
  moveButton: 'Move',
  movedToast: (count: number, path: string): string => `Moved ${pluralizeFiles(count)} to ${path}`,
  moveFailed: 'Failed to move files to the subfolder',
  moveForbidden: 'You do not have permission to move these files',
} as const

// Live checks on a file type name (AC4-5, AC4-6, D9). The backend runs the same
// four rules; these strings must match its messages.
export const FILE_TYPE_NAME_MESSAGES = {
  required: 'File Type is not valid.',
  unique: 'Please add a unique name.',
  oneSeparatorOnly: `Use one ${FILE_TYPE_PARENT_SEPARATOR} only.`,
  emptyPart: `Add a name on both sides of ${FILE_TYPE_PARENT_SEPARATOR}.`,
  alreadyParent: 'This is already a parent file type please choose another name.',
  parentIsFileType: (parent: string): string => `“${parent}” is already a file type, please choose another parent name.`,
} as const

export const SUBFOLDER_SETTING_COPY = {
  title: 'Enable subfolders',
  description: 'Allow record specific subfolders under file types in this workflow. Subfolders only exist on the record where they’re made and never change this list.',
  notes: [
    { title: 'On uploads', text: 'An optional Subfolder field appears under File Type.' },
    { title: 'On the Files tab', text: 'Users can create subfolders and move files into them.' },
    { title: 'When turned off', text: 'The field is hidden. Files already in subfolders keep them.' },
  ],
  updateFailed: 'Failed to update the subfolders setting',
} as const

export const SUBFOLDERS_SETTING_STATUS = { active: 'On', inactive: 'Off' } as const

export const PARENT_GUIDANCE_COPY = {
  title: `Group file types under a parent with ${FILE_TYPE_PARENT_SEPARATOR}`,
  example: { parent: 'Sales', fileType: 'Quotes', sibling: 'Contracts' },
  labels: { parent: 'Parent', separator: 'separator', fileType: 'File type' },
  rules: 'File types that share a parent are grouped on each record’s Files tab. No > puts the file type at the top level. Use only one > symbol. Parent names must match exactly to group together. To rename a parent, edit each file type that uses it.',
  parentsInUse: 'Parents in use',
  noParents: 'None yet',
  parentChip: (parent: string, count: number): string => `${parent} · ${count}`,
} as const

export const FILE_TYPE_NAME_BREAKDOWN_COPY = {
  inputLabel: 'File Type name',
  inputHintBefore: 'Optional: start with a parent name and',
  inputHintAfter: 'to group it, e.g. Sales>Quotes. One > only.',
  parentLabel: 'PARENT',
  fileTypeLabel: 'File type',
  noParent: 'None',
  existingParent: 'Matches an existing parent',
  newParent: 'New parent',
  topLevel: 'Top level in the Files tab',
  subFolder: 'Sub-Folder in Files Section',
  previewTitle: 'Preview in the Files tab',
  newBadge: 'New',
  existingBadge: 'Already exists',
  existingFileType: 'This file type already exists',
} as const
